import React from "react";
import { Calendar, CheckSquare, Sparkles, Pill, BarChart2 } from "lucide-react";

/**
 * Canonical kind-to-style helper. ANY calendar surface (Agenda, Day,
 * Week, Month, DaySheet, Today) reads from this single map so colors,
 * icons, and labels stay in sync across the app.
 *
 * Items types we surface on the calendar:
 *   event             — scheduled commitments (meetings, dinners)
 *   chore             — recurring family duties
 *   todo              — one-off tasks (includes the "errands" list)
 *   medication_dose   — generated from `medications`
 *   poll              — community decision items (closes_at)
 */
export const KIND_STYLE = {
  event:           { dot: "#E07A5F", icon: Calendar,    label: "Event" },
  chore:           { dot: "#7E9F76", icon: Sparkles,    label: "Chore" },
  todo:            { dot: "#7387A6", icon: CheckSquare, label: "To-do" },
  medication_dose: { dot: "#D9A14B", icon: Pill,        label: "Med" },
  poll:            { dot: "#9E7AC9", icon: BarChart2,   label: "Poll" },
};

export function kindOf(item) {
  return item?.type || "todo";
}

export function styleOf(item) {
  return KIND_STYLE[kindOf(item)] || KIND_STYLE.todo;
}

/**
 * Sort key for chronological item ordering. Returns null for items
 * with no time — callers should bucket those into a separate "no time"
 * section (Agenda) or skip (timed views).
 */
export function itemTimestamp(item) {
  return item?.start_at || item?.due_at || item?.closes_at || null;
}

/** True if the item is in a completed state (chore done, todo done, dose taken). */
export function isCompleted(item) {
  if (!item) return false;
  if (item.done === true) return true;
  if (item.status === "done" || item.status === "completed") return true;
  if (item.completed_at) return true;
  return false;
}

/** Small inline dot used by Week/Month grids. */
export function KindDot({ item, className = "" }) {
  const s = styleOf(item);
  return (
    <span
      className={`inline-block w-1.5 h-1.5 rounded-full ${className}`}
      style={{ backgroundColor: s.dot, opacity: isCompleted(item) ? 0.35 : 1 }}
    />
  );
}

/** Inline chip used by Day group lists + Agenda rows. */
export function KindChip({ item, time = true, className = "" }) {
  const s = styleOf(item);
  const Icon = s.icon;
  const completed = isCompleted(item);
  return (
    <span
      className={`inline-flex items-center gap-1.5 ${className}`}
      style={{ color: s.dot, opacity: completed ? 0.55 : 1 }}
    >
      <Icon className="w-3 h-3" strokeWidth={2.2} />
      {time && (
        <span className="text-[10px] uppercase tracking-wider font-medium">{s.label}</span>
      )}
    </span>
  );
}
